// Custom History API router (08-RESEARCH.md Pattern 5). No React-Router.
import {
  createContext,
  type JSX,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { FormattedMessage } from 'react-intl'
import { $returnTo } from '../stores/returnTo.js'
import { registerRouterNavigate, unregisterRouterNavigate } from './nav.js'

// ARCA-92 / WS1 — route access taxonomy for the router-level auth guard.
//   - 'public': always rendered (splash, about, privacy, tutorial, fallback).
//   - 'guest':  rendered for guests and authenticated users alike — anon-JWT
//               surfaces (bot practice + a session's own /match|/replay). The
//               guard never gates these; the anon JWT is minted on *entering
//               practice* (WS2), not here.
//   - 'auth':   rendered only for an authenticated session; a resolved guest
//               is redirected to /splash.
export type RouteAccess = 'public' | 'guest' | 'auth'

// Resolved auth state the guard reasons about (structurally mirrors
// AuthState['kind'] from AuthProvider; kept local so the generic router does
// not depend on the auth module).
export type RouterAuthKind = 'unknown' | 'guest' | 'authenticated'

export interface Route {
  pattern: RegExp
  render: (params: Record<string, string>) => ReactNode
  /**
   * Access classification consumed by the auth guard (ARCA-92 / WS1). Omitting
   * it defaults to 'public' (guard inert) — every app route in routes.tsx tags
   * itself explicitly; only standalone test harnesses rely on the default.
   */
  access?: RouteAccess
}

interface RouterValue {
  path: string
  navigate: (p: string) => void
}

const RouterContext = createContext<RouterValue | null>(null)

function extractParams(pattern: RegExp, path: string): Record<string, string> {
  const m = pattern.exec(path)
  return m?.groups ? { ...m.groups } : {}
}

export function Router({
  routes,
  initialPath,
  authKind,
}: {
  routes: Route[]
  initialPath?: string
  /**
   * Resolved auth state for the auth guard (ARCA-92 / WS1). RootApp passes
   * `auth.kind`; when omitted the guard is inert (used by standalone tests
   * that render the router without an AuthProvider above it).
   */
  authKind?: RouterAuthKind
}): JSX.Element {
  const [path, setPath] = useState(
    initialPath ??
      (typeof window !== 'undefined' ? window.location.pathname || '/' : '/'),
  )
  useEffect(() => {
    const onPopState = (): void => setPath(window.location.pathname)
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [])
  const navigate = useCallback((p: string) => {
    window.history.pushState(null, '', p)
    setPath(p)
  }, [])
  // Bridge for AuthProvider (sits above Router; cannot consume useRouter()).
  useEffect(() => {
    registerRouterNavigate(navigate)
    return () => unregisterRouterNavigate(navigate)
  }, [navigate])
  // Phase 18 Plan 03 — POLISH-08 focus-on-navigate.
  // On every path change focus the new screen's <main id="main" tabIndex={-1}>
  // so screen readers re-announce the page. queueMicrotask (not setTimeout)
  // schedules the focus after the new route's React tree has mounted but
  // before the next paint.
  //
  // Subtlety: :focus-visible does NOT match programmatic focus (CSS spec)
  // — so the user sees NO ring on <main>. This is the intended behavior;
  // the SR announcement is the user-facing effect. RESEARCH §Pitfall 2.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `path` is the trigger; the effect body intentionally does not read it.
  useEffect(() => {
    queueMicrotask(() => {
      const main = document.getElementById('main')
      if (main) main.focus({ preventScroll: false })
    })
  }, [path])
  const matched = routes.find((r) => r.pattern.test(path))
  const params = matched ? extractParams(matched.pattern, path) : {}

  // ARCA-92 / WS1 — router-level auth route guard.
  //
  // A matched route tagged `access: 'auth'` renders only for an authenticated
  // session:
  //   - 'authenticated' → render the route normally.
  //   - 'guest'         → redirect to /splash (in an effect, never during
  //                       render), stashing the intended path in $returnTo so
  //                       WS2 can resume after sign-in. We render the neutral
  //                       pending state in the meantime so the protected
  //                       screen never flashes before the redirect lands.
  //   - 'unknown'       → silent refresh still in flight: render the neutral
  //                       pending state and WAIT. Do NOT redirect — gating on a
  //                       half-resolved auth state would bounce a user who is
  //                       in fact authenticated (no redirect flicker).
  // Routes without `access` default to 'public', so the guard is inert for
  // them and for any caller that does not pass `authKind`.
  const requiresAuth = matched?.access === 'auth'
  const isAuthenticated = authKind === 'authenticated'
  const redirectGuestToSplash = requiresAuth && authKind === 'guest'

  useEffect(() => {
    if (redirectGuestToSplash) {
      // Stash where the guest was headed *before* we change the path; WS2
      // reads this on sign-in to resume navigation.
      $returnTo.set(path)
      navigate('/splash')
    }
  }, [redirectGuestToSplash, path, navigate])

  let content: ReactNode
  if (!matched) {
    content = <NotFound />
  } else if (requiresAuth && !isAuthenticated) {
    // Guest (mid-redirect) or 'unknown' (resolving): show the neutral loader,
    // never the protected screen.
    content = <RouteGuardPending />
  } else {
    content = matched.render(params)
  }

  return (
    <RouterContext.Provider value={{ path, navigate }}>
      {content}
    </RouterContext.Provider>
  )
}

// ARCA-92 / WS1 — neutral loading state shown while an auth-required route
// resolves its session (silent refresh in flight) or while a resolved guest is
// being redirected to /splash. Carries the <main id="main" tabIndex={-1}> seam
// so the POLISH-08 focus-on-navigate effect still lands and screen readers get
// a neutral announcement instead of any protected content.
function RouteGuardPending(): JSX.Element {
  return (
    <main id="main" tabIndex={-1} data-screen="auth-pending" aria-busy="true">
      <FormattedMessage id="app.loading" defaultMessage="Loading…" />
    </main>
  )
}

export function useRouter(): RouterValue {
  const ctx = useContext(RouterContext)
  if (!ctx) throw new Error('useRouter must be used inside <Router />')
  return ctx
}

// Plan 08-07 — localized 404 copy.
function NotFound(): JSX.Element {
  return (
    <main data-screen="not-found">
      <h1>
        <FormattedMessage
          id="errors.not_found"
          defaultMessage="Page not found."
        />
      </h1>
    </main>
  )
}
